import { SelectOption } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useTranslation } from 'core/i18n/i18n';
import { useMemo } from 'react';
import { RangeMode, ResultType, TestSummary } from 'modules/settings/pods/settings-materials/material-definition.vm';
import {
    QcLevelEditableField,
    TestConfigFormItem,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import { QcTargetErrors } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.validation';
import {
    QcConfigError,
    QcConfigList,
    QcConfigSection,
    QcConfigSectionHeader,
    QcConfigSectionSubtitle,
} from './qc-config-card.style';
import { TestConfigCard } from './test-config-card.component';

const QC_CONFIG_KEY = 'settings.materials.material-definitions.form.qc-config';
const TEST_CONFIG_KEY = 'settings.materials.material-definitions.form.test-config';

interface QcConfigCardProps {
    testConfigs: TestConfigFormItem[];
    tests: TestSummary[];
    onAddTest: VoidFunction;
    onRemoveTest: (clientKey: string) => void;
    onTestChange: (clientKey: string, testId: string) => void;
    onResultTypeChange: (clientKey: string, resultType: ResultType) => void;
    onPossibleValuesChange: (clientKey: string, possibleValues: string) => void;
    onRangeModeChange: (clientKey: string, rangeMode: RangeMode) => void;
    onAddLevel: (clientKey: string, defaultName: string) => void;
    onRemoveLevel: (clientKey: string, index: number) => void;
    onLevelChange: (clientKey: string, index: number, field: QcLevelEditableField, value: string | null) => void;
    error?: string;
    targetErrors?: QcTargetErrors;
}

export const QcConfigCard: React.FC<QcConfigCardProps> = ({
    testConfigs,
    tests,
    onAddTest,
    onRemoveTest,
    onTestChange,
    onResultTypeChange,
    onPossibleValuesChange,
    onRangeModeChange,
    onAddLevel,
    onRemoveLevel,
    onLevelChange,
    error,
    targetErrors,
}) => {
    const { t } = useTranslation();

    const testNameById = useMemo(() => new Map(tests.map((test) => [test.id, test.name])), [tests]);

    const resultTypeOptions = useMemo<SelectOption[]>(
        () => [
            { value: 'QUANTITATIVE', text: t(`${QC_CONFIG_KEY}.result-type.quantitative`) },
            { value: 'QUALITATIVE', text: t(`${QC_CONFIG_KEY}.result-type.qualitative`) },
        ],
        [t]
    );

    const testOptionsFor = (currentClientKey: string): SelectOption[] => {
        const chosenElsewhere = new Set(
            testConfigs
                .filter((testConfig) => testConfig.clientKey !== currentClientKey && testConfig.testId !== '')
                .map((testConfig) => testConfig.testId)
        );
        return tests
            .filter((test) => !chosenElsewhere.has(test.id))
            .map((test) => ({ value: test.id, text: test.name }));
    };

    return (
        <QcConfigSection>
            <QcConfigSectionHeader>
                <PocTypography variant="subtitle1">{t(`${TEST_CONFIG_KEY}.section-title`)}</PocTypography>
                <QcConfigSectionSubtitle>
                    <PocTypography variant="body2">{t(`${TEST_CONFIG_KEY}.section-subtitle`)}</PocTypography>
                </QcConfigSectionSubtitle>
            </QcConfigSectionHeader>
            {error && <QcConfigError data-testid="qc-test-config-error">{t(error)}</QcConfigError>}
            <QcConfigList>
                {testConfigs.map((testConfig, cardIndex) => (
                    <TestConfigCard
                        key={testConfig.clientKey}
                        testConfig={testConfig}
                        cardIndex={cardIndex}
                        testName={testNameById.get(testConfig.testId)}
                        testOptions={testOptionsFor(testConfig.clientKey)}
                        resultTypeOptions={resultTypeOptions}
                        onRemoveTest={onRemoveTest}
                        onTestChange={onTestChange}
                        onResultTypeChange={onResultTypeChange}
                        onPossibleValuesChange={onPossibleValuesChange}
                        onRangeModeChange={onRangeModeChange}
                        onAddLevel={onAddLevel}
                        onRemoveLevel={onRemoveLevel}
                        onLevelChange={onLevelChange}
                        targetErrors={targetErrors?.[testConfig.clientKey]}
                    />
                ))}
                <PocButton variant="secondary" data-testid="qc-test-add" onClick={onAddTest}>
                    {t(`${TEST_CONFIG_KEY}.add-test`)}
                </PocButton>
            </QcConfigList>
        </QcConfigSection>
    );
};
